import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { AlertTriangle, Check, Lock, Shield } from 'lucide-react';

// Admin-2FA (TOTP): Status anzeigen, einrichten und sicher rotieren. Für Admin-Konten ist
// der zweite Faktor verbindlich; der Login kann dieses Bauteil mit einem kurzlebigen,
// ausschließlich für die Einrichtung gültigen Token öffnen.
export default function TwoFactor({ required = false, setupToken = null, onComplete = null }) {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null); // { qr, secret, otpauth_url }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null); // string[] | null (einmalig anzeigen)
  const requestConfig = setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : null;
  const get = (path) => requestConfig ? api.get(path, requestConfig) : api.get(path);
  const post = (path, body = {}) => requestConfig ? api.post(path, body, requestConfig) : api.post(path, body);

  // Recovery-Codes erzeugen (ersetzt alte) — braucht den aktuellen Authenticator-Code als Identitätsbeweis.
  const genRecovery = async () => {
    const totp = window.prompt('Recovery-Codes erzeugen: bitte den aktuellen 6-stelligen Authenticator-Code eingeben:');
    if (!totp) return;
    setBusy(true);
    try { const r = await post('/auth/2fa/recovery/generate', { totp }); setRecoveryCodes(r.data.codes); toast.success('Neue Recovery-Codes erzeugt'); }
    catch (e) { toast.error(e.response?.data?.error || 'Code ungültig'); }
    finally { setBusy(false); }
  };

  const load = () => get('/auth/2fa/status').then(r => setEnabled(r.data.enabled)).catch(() => {});
  useEffect(() => { load(); }, [setupToken]);

  const startSetup = async () => {
    let setupProof = {};
    if (enabled === true) {
      const currentCode = window.prompt('Authenticator wechseln: aktuellen 6-stelligen Authenticator-Code oder einen Recovery-Code eingeben:');
      if (!currentCode) return;
      setupProof = { totp: currentCode, recovery_code: currentCode };
    }
    setBusy(true);
    try {
      let response;
      try {
        response = await post('/auth/2fa/setup', setupProof);
      } catch (error) {
        if (error.response?.data?.error !== 'mfa_enrollment_required') throw error;
        const code = window.prompt(`Sicherheitscode aus der E-Mail an ${error.response.data.email || 'deine hinterlegte Adresse'} oder Recovery-Code eingeben:`);
        if (!code) return;
        response = await post('/auth/2fa/setup', { mfa_enrollment_code: code, recovery_code: code });
      }
      setSetup(response.data);
    }
    catch (e) { toast.error(e.response?.data?.error || 'Konnte 2FA nicht einrichten'); }
    finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      const response = await post('/auth/2fa/enable', { totp: code });
      if (setupToken) {
        if (typeof response.data?.token !== 'string') throw new Error('Vollständige Admin-Sitzung fehlt');
        localStorage.setItem('admin_token', response.data.token);
      }
      toast.success('2FA aktiviert'); setSetup(null); setCode(''); setEnabled(true);
      onComplete?.(response.data);
    }
    catch (e) { toast.error(e.response?.data?.error || 'Code ungültig'); }
    finally { setBusy(false); }
  };

  const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 24, maxWidth: 520 };
  const btn = (bg, color = '#fff') => ({ padding: '11px 18px', background: bg, color, border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' });

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <Shield size={20} color="#9C27B0" />
        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a' }}>Zwei-Faktor-Authentifizierung</h2>
      </div>
      <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
        Schützt dein Admin-Konto mit einem Einmal-Code aus einer Authenticator-App
        (<b>Microsoft Authenticator</b>, Google Authenticator, Authy …). Selbst bei gestohlenem Passwort kommt niemand ohne dein Handy rein.
      </p>
      {required && enabled === false && !setup && (
        <p style={{ color: '#7b1fa2', fontSize: 13, lineHeight: 1.6, marginBottom: 16, fontWeight: 700 }}>
          Die Anmeldung wird abgeschlossen, sobald du deinen Authenticator verbunden hast.
        </p>
      )}

      {enabled === true && !setup && (
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#f3e5f5', color: '#7b1fa2', padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700, marginBottom: 16 }}>
            <Check size={16} /> 2FA ist aktiv
          </div>

          <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, color: '#0a0a0a', marginBottom: 6 }}>Authenticator wechseln</h3>
            <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
              Verbinde eine neue Authenticator-App. Der bisherige Faktor bleibt aktiv, bis du den neuen Code bestätigt hast.
            </p>
            <button onClick={startSetup} disabled={busy} style={btn('#0a0a0a')}>Authenticator wechseln</button>
          </div>

          {/* Recovery-Codes: Backup, falls das Handy/Authenticator verloren geht. */}
          <div style={{ borderTop: '1px solid #eee', paddingTop: 16, marginBottom: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, color: '#0a0a0a', marginBottom: 6 }}>Recovery-Codes</h3>
            <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
              Einmal-Codes als Backup: damit kommst du auch dann ins Konto, wenn du dein Handy/den Authenticator verlierst.
              <b> Sicher aufbewahren</b> (Passwort-Manager/ausgedruckt). Jeder Code funktioniert genau einmal.
            </p>
            {recoveryCodes ? (
              <div>
                <div style={{ background: '#0a0a0a', color: '#9C27B0', borderRadius: 10, padding: 16, fontFamily: 'monospace', fontSize: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                  {recoveryCodes.map((c, i) => <div key={i}>{c}</div>)}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
                  <button onClick={() => { navigator.clipboard?.writeText(recoveryCodes.join('\n')); toast.success('Codes kopiert'); }} style={btn('#0a0a0a')}>Kopieren</button>
                  <button onClick={() => setRecoveryCodes(null)} style={{ ...btn('transparent', '#4b5563'), padding: '11px 0' }}>Fertig (ausgeblendet)</button>
                  <span style={{ color: '#4b5563', fontSize: 12, fontWeight: 600 }}><AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> Werden nur EINMAL angezeigt</span>
                </div>
              </div>
            ) : (
              <button onClick={genRecovery} disabled={busy} style={btn('#0a0a0a')}>Recovery-Codes erzeugen</button>
            )}
          </div>

          <p style={{ color: '#4b5563', fontSize: 13 }}>Für Admin-Konten ist 2FA verpflichtend.</p>
        </div>
      )}

      {enabled === false && !setup && (
        <button onClick={startSetup} disabled={busy} style={btn('#0a0a0a')}><Lock size={15} style={{ verticalAlign: -2, marginRight: 6 }} />2FA einrichten</button>
      )}

      {setup && (
        <div>
          <p style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>1. Scanne den QR-Code mit deiner Authenticator-App:</p>
          <img src={setup.qr} alt="2FA QR" style={{ width: 200, height: 200, border: '1px solid #e5e7eb', borderRadius: 12 }} />
          <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 16px' }}>Falls Scannen nicht geht, Schlüssel manuell eingeben: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{setup.secret}</code></p>
          <p style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>2. Code aus der App eingeben:</p>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} maxLength={6} inputMode="numeric" placeholder="123456"
            style={{ width: 160, padding: '11px 14px', border: '1px solid #9C27B0', borderRadius: 9, fontSize: 16, letterSpacing: 6, textAlign: 'center', marginRight: 10 }} />
          <button onClick={enable} disabled={busy || code.length !== 6} style={btn(code.length === 6 ? '#9C27B0' : '#d1d5db')}>Aktivieren</button>
          {!required && <div style={{ marginTop: 12 }}><button onClick={() => { setSetup(null); setCode(''); }} style={{ ...btn('transparent', '#4b5563'), padding: 0 }}>Abbrechen</button></div>}
        </div>
      )}

      {enabled === null && <p style={{ color: '#6b7280', fontSize: 13 }}>Lädt …</p>}
    </div>
  );
}
